// GS1 barcode utilities — GTIN/UPC/EAN/ISBN check digits and prefix lookup.
// All client-side, no network calls.

// GS1 mod-10 check digit: weights alternate 3,1,3,1... from the rightmost
// digit (excluding the check digit itself).
export function gs1CheckDigit(digits) {
  const clean = digits.replace(/\D/g, '');
  let sum = 0;
  for (let i = 0; i < clean.length; i++) {
    const d = clean.charCodeAt(clean.length - 1 - i) - 48;
    sum += d * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

const FORMATS = {
  8: 'EAN-8 / GTIN-8',
  12: 'UPC-A / GTIN-12',
  13: 'EAN-13 / GTIN-13',
  14: 'GTIN-14 / ITF-14',
};

// GS1 prefix ranges → issuing member organization (major ranges)
const GS1_PREFIXES = [
  [0, 19, 'GS1 US (UPC-A)'],
  [20, 29, 'Restricted distribution (in-store)'],
  [30, 39, 'GS1 US (drugs/health)'],
  [40, 49, 'Restricted distribution (in-store)'],
  [50, 59, 'Coupons / GS1 US'],
  [60, 139, 'GS1 US'],
  [200, 299, 'Restricted distribution (in-store)'],
  [300, 379, 'GS1 France'],
  [380, 380, 'GS1 Bulgaria'],
  [383, 383, 'GS1 Slovenia'],
  [385, 385, 'GS1 Croatia'],
  [387, 387, 'GS1 Bosnia & Herzegovina'],
  [389, 389, 'GS1 Montenegro'],
  [390, 390, 'GS1 Kosovo'],
  [400, 440, 'GS1 Germany'],
  [450, 459, 'GS1 Japan'],
  [460, 469, 'GS1 Russia'],
  [470, 470, 'GS1 Kyrgyzstan'],
  [471, 471, 'GS1 Taiwan'],
  [474, 474, 'GS1 Estonia'],
  [475, 475, 'GS1 Latvia'],
  [476, 476, 'GS1 Azerbaijan'],
  [477, 477, 'GS1 Lithuania'],
  [478, 478, 'GS1 Uzbekistan'],
  [479, 479, 'GS1 Sri Lanka'],
  [480, 480, 'GS1 Philippines'],
  [481, 481, 'GS1 Belarus'],
  [482, 482, 'GS1 Ukraine'],
  [483, 483, 'GS1 Turkmenistan'],
  [484, 484, 'GS1 Moldova'],
  [485, 485, 'GS1 Armenia'],
  [486, 486, 'GS1 Georgia'],
  [487, 487, 'GS1 Kazakhstan'],
  [488, 488, 'GS1 Tajikistan'],
  [489, 489, 'GS1 Hong Kong'],
  [490, 499, 'GS1 Japan'],
  [500, 509, 'GS1 UK'],
  [520, 521, 'GS1 Global Office (GTIN-8)'],
  [528, 528, 'GS1 Lebanon'],
  [529, 529, 'GS1 Cyprus'],
  [530, 530, 'GS1 Albania'],
  [531, 531, 'GS1 North Macedonia'],
  [535, 535, 'GS1 Malta'],
  [539, 539, 'GS1 Ireland'],
  [540, 549, 'GS1 Belgium & Luxembourg'],
  [560, 560, 'GS1 Portugal'],
  [569, 569, 'GS1 Iceland'],
  [570, 579, 'GS1 Denmark'],
  [590, 590, 'GS1 Poland'],
  [594, 594, 'GS1 Romania'],
  [599, 599, 'GS1 Hungary'],
  [600, 601, 'GS1 South Africa'],
  [603, 603, 'GS1 Ghana'],
  [604, 604, 'GS1 Senegal'],
  [608, 608, 'GS1 Bahrain'],
  [609, 609, 'GS1 Mauritius'],
  [611, 611, 'GS1 Morocco'],
  [613, 613, 'GS1 Algeria'],
  [615, 615, 'GS1 Nigeria'],
  [616, 616, 'GS1 Kenya'],
  [618, 618, 'GS1 Ivory Coast'],
  [619, 619, 'GS1 Tunisia'],
  [620, 620, 'GS1 Tanzania'],
  [621, 621, 'GS1 Syria'],
  [622, 622, 'GS1 Egypt'],
  [623, 623, 'GS1 Brunei'],
  [624, 624, 'GS1 Libya'],
  [625, 625, 'GS1 Jordan'],
  [626, 626, 'GS1 Iran'],
  [627, 627, 'GS1 Kuwait'],
  [628, 628, 'GS1 Saudi Arabia'],
  [629, 629, 'GS1 UAE'],
  [630, 630, 'GS1 Qatar'],
  [631, 631, 'GS1 Namibia'],
  [640, 649, 'GS1 Finland'],
  [690, 699, 'GS1 China'],
  [700, 709, 'GS1 Norway'],
  [729, 729, 'GS1 Israel'],
  [730, 739, 'GS1 Sweden'],
  [740, 740, 'GS1 Guatemala'],
  [741, 741, 'GS1 El Salvador'],
  [742, 742, 'GS1 Honduras'],
  [743, 743, 'GS1 Nicaragua'],
  [744, 744, 'GS1 Costa Rica'],
  [745, 745, 'GS1 Panama'],
  [746, 746, 'GS1 Dominican Republic'],
  [750, 750, 'GS1 Mexico'],
  [754, 755, 'GS1 Canada'],
  [759, 759, 'GS1 Venezuela'],
  [760, 769, 'GS1 Switzerland'],
  [770, 771, 'GS1 Colombia'],
  [773, 773, 'GS1 Uruguay'],
  [775, 775, 'GS1 Peru'],
  [777, 777, 'GS1 Bolivia'],
  [778, 779, 'GS1 Argentina'],
  [780, 780, 'GS1 Chile'],
  [784, 784, 'GS1 Paraguay'],
  [786, 786, 'GS1 Ecuador'],
  [789, 790, 'GS1 Brazil'],
  [800, 839, 'GS1 Italy'],
  [840, 849, 'GS1 Spain'],
  [850, 850, 'GS1 Cuba'],
  [858, 858, 'GS1 Slovakia'],
  [859, 859, 'GS1 Czech Republic'],
  [860, 860, 'GS1 Serbia'],
  [865, 865, 'GS1 Mongolia'],
  [867, 867, 'GS1 North Korea'],
  [868, 869, 'GS1 Turkey'],
  [870, 879, 'GS1 Netherlands'],
  [880, 880, 'GS1 South Korea'],
  [883, 883, 'GS1 Myanmar'],
  [884, 884, 'GS1 Cambodia'],
  [885, 885, 'GS1 Thailand'],
  [888, 888, 'GS1 Singapore'],
  [890, 890, 'GS1 India'],
  [893, 893, 'GS1 Vietnam'],
  [896, 896, 'GS1 Pakistan'],
  [899, 899, 'GS1 Indonesia'],
  [900, 919, 'GS1 Austria'],
  [930, 939, 'GS1 Australia'],
  [940, 949, 'GS1 New Zealand'],
  [950, 950, 'GS1 Global Office'],
  [955, 955, 'GS1 Malaysia'],
  [958, 958, 'GS1 Macau'],
  [960, 969, 'GS1 Global Office (GTIN-8)'],
  [977, 977, 'Serial publications (ISSN)'],
  [978, 979, 'Bookland (ISBN)'],
  [980, 980, 'Refund receipts'],
  [981, 984, 'Coupon identification'],
  [990, 999, 'Coupon identification'],
];

export function gs1PrefixLookup(code) {
  const prefix = parseInt(code.slice(0, 3), 10);
  for (const [lo, hi, name] of GS1_PREFIXES) {
    if (prefix >= lo && prefix <= hi) return { prefix: code.slice(0, 3), name };
  }
  return { prefix: code.slice(0, 3), name: 'Unallocated / unknown prefix' };
}

export function validateGtin(input) {
  const code = input.replace(/[\s-]/g, '');
  const result = { code, checks: [], valid: false, format: null, prefix: null };

  if (!code) {
    result.checks.push({ label: 'Input', ok: false, detail: 'Empty input' });
    return result;
  }
  if (!/^\d+$/.test(code)) {
    result.checks.push({ label: 'Characters', ok: false, detail: 'Digits only (spaces and hyphens are stripped)' });
    return result;
  }
  result.checks.push({ label: 'Characters', ok: true, detail: 'All digits' });

  const format = FORMATS[code.length];
  if (!format) {
    result.checks.push({ label: 'Length', ok: false, detail: `${code.length} digits — expected 8, 12, 13, or 14` });
    return result;
  }
  result.format = format;
  result.checks.push({ label: 'Format', ok: true, detail: `${code.length} digits → ${format}` });

  const expected = gs1CheckDigit(code.slice(0, -1));
  const actual = parseInt(code.slice(-1), 10);
  const ok = expected === actual;
  result.checks.push({
    label: 'GS1 check digit',
    ok,
    detail: ok ? `Check digit ${actual} is correct` : `Expected ${expected}, found ${actual} — possible typo`,
  });

  if (code.length >= 12) {
    result.prefix = gs1PrefixLookup(code);
  }
  if (code.length === 13 && code.startsWith('978')) {
    result.isbn = code;
  }

  result.valid = ok;
  return result;
}

// ISBN-10: sum of digit×weight (10..1) must be 0 mod 11; last char may be 'X'
export function validateIsbn10(input) {
  const code = input.replace(/[\s-]/g, '').toUpperCase();
  if (!/^\d{9}[\dX]$/.test(code)) return { valid: false, detail: 'Must be 9 digits + check digit (0-9 or X)' };
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const d = code[i] === 'X' ? 10 : code.charCodeAt(i) - 48;
    sum += d * (10 - i);
  }
  const valid = sum % 11 === 0;
  return { valid, detail: valid ? 'ISBN-10 checksum valid' : 'ISBN-10 checksum invalid' };
}

export function isbn10to13(isbn10) {
  const base = '978' + isbn10.slice(0, 9);
  return base + gs1CheckDigit(base);
}
